import layout from './components/agentLayout.module.css';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Checkbox,
  Col,
  Divider,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Typography,
  message,
} from 'antd';

import { parsePhoneNumber } from 'libphonenumber-js';
import 'react-phone-input-2/lib/style.css';

import CustomInput from '@/components/CustomInput';
import { CUSTOM_VAR_IN_USE_ERROR } from '@/components/CheckboxMultiSelect';
import { CUSTOM_VAR_EDIT_CANCELLED } from '@/components/CustomVariableTag';
import CustomVariableUsageWarningModal, {
  type AffectedWorkflow,
} from '@/components/CustomVariableUsageWarningModal';

import {
  CallFillIcon,
  MailFillIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@/assets/icons/icons';

import type {
  AgentDrawerFormValues,
  SettingsAgentDrawerFormProps,
  ChannelType,
  CreateAgentPayload,
  VoiceOption,
  ApiVoice,
  Direction,
  AgentTemplate,
  NewCustomVarInput,
} from './types';
import { menuSpeech } from './phoneTreeKeys';

import './AgentsTable.css';

import {
  CreateAgent,
  UpdateAgent,
  PostAiAgentDynamicVariablesUsed,
  TestAgent,
  TestEmailAgent,
  GetAgentTemplates,
  ImproveAgentPrompt,
  ImproveResolutionPrompt,
  GetTestAgentResult,
  GetSettingsProfile,
  GetCompnayInfo,
  GetCompanyPhoneNumbers,
  GetAiAgentVoicesWithSearchAndPagination,
  DeleteCustomCollectVar,
  UpdateCustomCollectVar,
  GetCustomCollectVarUsage,
} from '@/apis/settings';
import type { CompanyPhoneNumberOption } from '@/apis/settings';

import DynamicVariablesModal from './DynamicVariablesModal';
import { useDynamicVariables } from './hooks/useDynamicVariables';

import {
  buildCreatePayload,
  buildIntroPreview,
  buildEmailSignaturePreview,
  buildTestPayload,
  buildTestEmailPayload,
  getDefaultFormValues,
  mapEditAgentToFormValues,
} from './agentFormMappers';

import { useAgentDropdowns } from './hooks/useAgentsDropdowns';
import { useAgentDetails } from './hooks/useAgentDetails';
import { useVoicePreview } from './hooks/useVoicePreview';

import { AgentHeader, SetupAgentTestModal } from './components/AgentHeader';
import { IdentitySection } from './components/IdentitySection';
import { VoiceSection } from './components/VoiceSection';
import { EscalationSection } from './components/EscalateSection';
import { PhoneTreeSection } from './components/PhoneTreeSection';
import {
  validateBehaviorInstructions,
  validateSuccessCriteria,
  shouldPromptForInstructionsBeforeSave,
} from './components/defaultAgentGuards';
import { SuccessSection } from './components/SuccessSection';
import { AgentFooter } from './components/AgentFooter';
import { useToken } from '@/common/helper';
import AgentBehaviorSection from './components/AgentBehaviorSection';
import AgentInstructionsModal, {
  GenerateResp,
} from './components/AgentInstructionsModal';
import { CustomTitle, ToggleSwitch } from '@/components';

const { Paragraph } = Typography;

export type AgentDrawerFormRef = {
  validate: () => Promise<AgentDrawerFormValues>;
  getCreatePayload: () => Promise<CreateAgentPayload & { id?: string | null }>;
  hasUnsavedChanges: () => boolean;
  triggerTest: () => Promise<void>;
};

type ChannelHeaderProps = {
  icon: React.ReactNode;
  title: string;
  enabled: boolean;
  expanded: boolean;
  onToggleEnabled: (v: boolean) => void;
  onToggleExpanded: () => void;
  readOnly?: boolean;
};

const ChannelHeader: React.FC<ChannelHeaderProps> = ({
  icon,
  title,
  enabled,
  expanded,
  onToggleEnabled,
  onToggleExpanded,
  readOnly = false,
}) => {
  return (
    <div
      className='agentChannelHeader'
      role='button'
      tabIndex={0}
      onClick={onToggleExpanded}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onToggleExpanded();
      }}
    >
      <div className='agentChannelHeader__left'>
        <span className='agentChannelHeader__icon'>{icon}</span>
        <span className='agentChannelHeader__title'>{title}</span>
      </div>

      <div
        className='agentChannelHeader__right'
        onClick={(e) => e.stopPropagation()}
      >
        <ToggleSwitch
          label=''
          checked={enabled}
          onChange={(e) => onToggleEnabled(e.target.checked)}
          disabled={readOnly}
        />
        <span
          className='agentChannelHeader__chevron'
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpanded();
          }}
          role='button'
          tabIndex={0}
        >
          {expanded ? (
            <ChevronUpIcon size={16} />
          ) : (
            <ChevronDownIcon size={16} />
          )}
        </span>
      </div>
    </div>
  );
};

function phoneOptionLabel(
  phoneNumber: string,
  label: string | null
): React.ReactNode {
  let flagClass = 'us';
  let display = phoneNumber;
  try {
    const parsed = parsePhoneNumber(phoneNumber);
    flagClass = (parsed.country ?? 'US').toLowerCase();
    display = `+${parsed.countryCallingCode} ${parsed.formatNational()}`;
  } catch {
    // fall through to raw number
  }
  return (
    <span className={layout.row}>
      <span
        className={['react-tel-input', layout.countryPicker]
          .filter(Boolean)
          .join(' ')}
      >
        <span
          className={[`flag ${flagClass}`, layout.inlineFlag]
            .filter(Boolean)
            .join(' ')}
        />
      </span>
      <span>{label ? `${display} (${label})` : display}</span>
    </span>
  );
}

type CallSectionProps = {
  expanded: boolean;
  isDefaultAgent: boolean;
  form: FormInstance<AgentDrawerFormValues>;
  introPreview: string;
  loadingDropdowns: boolean;
  presetVoice: VoiceOption | null;
  playingVoice: string | null;
  onToggleVoice: (voiceId: string) => void;
  escalateWhenOptions: { value: string; label: string }[];
  onToggleExpanded: () => void;
  enabled: boolean;
  onToggleEnabled: (v: boolean) => void;
  phoneNumberOptions: CompanyPhoneNumberOption[];
  showKeypadOption: boolean;
  readOnly?: boolean;
};

const CallSection: React.FC<CallSectionProps> = ({
  expanded,
  isDefaultAgent,
  form,
  introPreview,
  loadingDropdowns,
  presetVoice,
  playingVoice,
  onToggleVoice,
  escalateWhenOptions,
  onToggleExpanded,
  enabled,
  onToggleEnabled,
  phoneNumberOptions,
  showKeypadOption,
  readOnly = false,
}) => {
  const [phoneSelectOpen, setPhoneSelectOpen] = useState<boolean>(false);
  return (
    <Col span={24}>
      <Divider className='agentFormDivider' />
      <div className='agentChannelCard'>
        <ChannelHeader
          icon={<CallFillIcon size={16} />}
          title='Call'
          enabled={enabled}
          expanded={expanded}
          onToggleEnabled={onToggleEnabled}
          onToggleExpanded={onToggleExpanded}
          readOnly={readOnly || isDefaultAgent}
        />

        {!enabled && expanded && (
          <div className='agentChannelBody agentChannelBody--disabled'>
            <div className='agentChannelDisabledText'>
              Calls not enabled for this agent.
            </div>
          </div>
        )}

        {enabled && (
          <div
            className={['agentChannelBody', !expanded && layout.hidden]
              .filter(Boolean)
              .join(' ')}
          >
            <div className={layout.stack}>
              <Form.Item
                label='Primary AI phone number'
                className='custom-input form-input-style'
                name='phone_number'
                rules={[
                  {
                    required: enabled,
                    message: 'Please select a phone number',
                  },
                ]}
              >
                <Select
                  className='agentSelect agentSelect--darkText'
                  disabled={readOnly || isDefaultAgent || !enabled}
                  options={phoneNumberOptions.map((n) => ({
                    value: n.phone_number,
                    label: phoneOptionLabel(n.phone_number, n.label),
                  }))}
                  onChange={(value: string) =>
                    form.setFieldsValue({ phone_number: value })
                  }
                  onDropdownVisibleChange={setPhoneSelectOpen}
                  suffixIcon={
                    phoneSelectOpen ? (
                      <ChevronUpIcon size={16} />
                    ) : (
                      <ChevronDownIcon size={16} />
                    )
                  }
                  notFoundContent='No AI agent phone numbers available. Please contact support to get one set up.'
                />
              </Form.Item>

              <VoiceSection
                loadingDropdowns={loadingDropdowns}
                playingVoice={playingVoice}
                onToggleVoice={onToggleVoice}
                presetVoice={presetVoice}
                disabled={readOnly}
              />

              <Form.Item label='Call introduction preview'>
                <div className='agentFormIntroPreview'>
                  <Form.Item noStyle shouldUpdate>
                    {() => {
                      const menu = form.getFieldValue('phone_tree');
                      if (!menu?.enabled) return introPreview;
                      const spoken = menuSpeech(menu.options, menu.repeat_key);
                      if (menu.skip_intro) return spoken;
                      return `${menu.greeting?.trim() || introPreview} ${spoken}`;
                    }}
                  </Form.Item>
                </div>
              </Form.Item>

              {!showKeypadOption && (
                <PhoneTreeSection
                  form={form}
                  disabled={readOnly || isDefaultAgent || !enabled}
                />
              )}

              {showKeypadOption && (
                <Form.Item
                  name='ivr_keypad_enabled'
                  valuePropName='checked'
                  extra='Lets the agent press keys to move through an automated phone menu, for example "press 0 for an operator".'
                >
                  <Checkbox disabled={readOnly || isDefaultAgent}>
                    Allow phone menu navigation
                  </Checkbox>
                </Form.Item>
              )}

              <EscalationSection
                loadingDropdowns={loadingDropdowns}
                form={form}
                escalateWhenOptions={escalateWhenOptions}
                isDefaultAgent={isDefaultAgent}
                readOnly={readOnly}
              />
            </div>
          </div>
        )}
      </div>
    </Col>
  );
};

type EmailSectionProps = {
  expanded: boolean;
  isDefaultAgent: boolean;
  emailSignaturePreview: string;
  enabled: boolean;
  onToggleEnabled: (v: boolean) => void;
  onToggleExpanded: () => void;
  readOnly?: boolean;
};

const EmailSection: React.FC<EmailSectionProps> = ({
  expanded,
  isDefaultAgent,
  emailSignaturePreview,
  enabled,
  onToggleEnabled,
  onToggleExpanded,
  readOnly = false,
}) => {
  return (
    <Col span={24}>
      <Divider className='agentFormDivider' />
      <div className='agentChannelCard'>
        <ChannelHeader
          icon={<MailFillIcon size={16} />}
          title='Email'
          enabled={enabled}
          expanded={expanded}
          onToggleEnabled={onToggleEnabled}
          onToggleExpanded={onToggleExpanded}
          readOnly={readOnly || isDefaultAgent}
        />

        {!enabled && expanded && (
          <div className='agentChannelBody agentChannelBody--disabled'>
            <div className='agentChannelDisabledText'>
              Email not enabled for this agent.
            </div>
          </div>
        )}

        {enabled && (
          <div
            className={['agentChannelBody', !expanded && layout.hidden]
              .filter(Boolean)
              .join(' ')}
          >
            <div className={layout.stack}>
              <Form.Item
                label='Email'
                className='custom-input form-input-style'
                name='email'
                rules={[
                  ...(enabled
                    ? [
                        { required: true, message: 'Please enter email' },
                        { type: 'email' as const, message: 'Invalid email' },
                      ]
                    : []),
                ]}
              >
                <CustomInput
                  bgColor='var(--light-gray)'
                  height='36px'
                  textColor='var(--text-primary)'
                  borderRadius='8px'
                  border='none'
                  placeholderColor='var(--text-secondary)'
                  disabled={true}
                  placeholder='customer@zoomlogi.com'
                />
              </Form.Item>

              <Form.Item label='Email signature preview'>
                <div
                  className={['agentFormIntroPreview', layout.preLine]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {emailSignaturePreview}
                </div>
              </Form.Item>

              <Form.Item
                label="CC'ed email"
                className='custom-input form-input-style'
                name='cc_email'
                rules={
                  enabled ? [{ type: 'email', message: 'Invalid email' }] : []
                }
              >
                <CustomInput
                  placeholder='john@customer.com'
                  bgColor='var(--light-gray)'
                  height='36px'
                  textColor='var(--text-primary)'
                  borderRadius='8px'
                  border='none'
                  placeholderColor='var(--text-secondary)'
                  disabled={readOnly || isDefaultAgent || !enabled}
                />
              </Form.Item>
              <Paragraph
                className='agentFormHelpText'
                style={{ position: 'relative', top: -16 }}
              >
                CC&apos;d on all messages. Reply in the thread anytime to stop
                AI actions
              </Paragraph>
            </div>
          </div>
        )}
      </div>
    </Col>
  );
};

const SettingsAgentDrawerForm = forwardRef(function SettingsAgentDrawerForm(
  {
    agentData,
    drawerOpen,
    onClose,
    onSaved,
    initialChannelType,
    readOnly = false,
    hideAgentHeader = false,
    onAgentTypeLoaded,
    onTestLoadingChange,
  }: SettingsAgentDrawerFormProps,
  ref: React.ForwardedRef<AgentDrawerFormRef>
) {
  const [form] = Form.useForm<AgentDrawerFormValues>();
  const isEditing = !!agentData;
  const [successCollapsed, setSuccessCollapsed] = useState(true);
  const [successKey, setSuccessKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const selectedCreateType = initialChannelType;
  const genReqIdRef = useRef(0);
  const isDefaultAgent =
    agentData?.purpose_name?.toLowerCase?.() === 'default' ||
    agentData?.purpose_name?.toLowerCase?.() === 'default platform agent';

  const initialApiChannelType: ChannelType = useMemo(() => {
    const first = Array.isArray(agentData?.channel)
      ? agentData.channel[0]
      : null;
    const apiType =
      (first?.type as ChannelType | undefined) ||
      (agentData?.type as ChannelType | undefined);

    if (apiType === 'email') return 'email';
    if (apiType === 'phone') return 'phone';
    return 'phone';
  }, [agentData]);

  const createDefaultType: ChannelType = useMemo(() => {
    if (initialChannelType === 'inbound') return 'phone';
    return 'phone';
  }, [initialChannelType]);

  const [channelType, setChannelType] = useState<ChannelType>(
    initialApiChannelType
  );

  const [callEnabled, setCallEnabled] = useState<boolean>(
    initialApiChannelType === 'phone'
  );
  const [emailEnabled, setEmailEnabled] = useState<boolean>(
    initialApiChannelType === 'email'
  );
  const [collectShipmentId, setCollectShipmentId] = useState<boolean>(false);
  const [instrOpen, setInstrOpen] = useState(false);
  const [callExpanded, setCallExpanded] = useState(false);
  const [emailExpanded, setEmailExpanded] = useState(false);

  const userChangedPurposeRef = useRef(false);
  const savedRef = useRef(false);
  const hydratedRef = useRef(false);
  const dirtyRef = useRef(false);

  const lockedChannelRef = useRef<ChannelType | null>(null);

  const watchedSelfIdentify = Form.useWatch('self_identify', form);
  const selfIdentify = watchedSelfIdentify ?? false;

  const watchedName = Form.useWatch('agent_name', form);
  const watchedRole = Form.useWatch('ai_agent_role', form);
  const watchedBehaviorInstructions = Form.useWatch(
    'behavior_instructions',
    form
  );

  const { decodedUserToken, token } = useToken();
  const { dynVarOptions } = useDynamicVariables(token);
  const decodedToken = decodedUserToken();
  const companyName: string | undefined = decodedToken?.company_name;

  const templateIdRef = useRef<string | null>(null);

  const {
    loadingDropdowns,
    dropdownsReady,
    collectOptions,
    setCollectOptions,
    escalateWhenOptions,
    purposeOptions,
    rawCollectData,
    rawEscalationData,
  } = useAgentDropdowns({ drawerOpen, token });

  // A queue, not a single slot, so editing a second variable while the
  // first edit's "this affects N automations" confirmation is still open
  // doesn't clobber the first one's resolve() - it's shown next instead.
  // Only the front entry is ever rendered/resolved.
  const [usageConfirmQueue, setUsageConfirmQueue] = useState<
    {
      variableName: string;
      workflows: AffectedWorkflow[];
      resolve: (proceed: boolean) => void;
    }[]
  >([]);
  const pendingUsageConfirm = usageConfirmQueue[0] ?? null;

  const confirmDespiteUsage = (
    variableName: string,
    workflows: AffectedWorkflow[]
  ): Promise<boolean> =>
    new Promise((resolve) => {
      setUsageConfirmQueue((queue) => [
        ...queue,
        { variableName, workflows, resolve },
      ]);
    });

  const resolveFrontUsageConfirm = (proceed: boolean) => {
    setUsageConfirmQueue((queue) => {
      queue[0]?.resolve(proceed);
      return queue.slice(1);
    });
  };

  // The popover context (and the token that would be passed to resolve())
  // is gone once the instructions modal closes - any edit still waiting on
  // a usage confirmation at that point can't be applied, so treat it as
  // cancelled rather than leaving it orphaned for a modal that no longer
  // exists.
  const cancelAllPendingUsageConfirms = () => {
    setUsageConfirmQueue((queue) => {
      queue.forEach((entry) => entry.resolve(false));
      return [];
    });
  };

  const [testLoading, setTestLoading] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [generatedEmailSubject, setGeneratedEmailSubject] = useState('');
  const [generatedEmailBody, setGeneratedEmailBody] = useState('');
  const [generatedCallTranscript, setGeneratedCallTranscript] = useState('');
  const [userName, setUserName] = useState<string>('');
  const [aiCompanyName, setAiCompanyName] = useState<string>('');
  const [phoneNumberOptions, setPhoneNumberOptions] = useState<
    CompanyPhoneNumberOption[]
  >([]);

  const [testResultsPolling, setTestResultsPolling] = useState(false);
  const [testResults, setTestResults] = useState<{
    status: string;
    issue_resolved: boolean;
    pca_results: Record<string, unknown> | null;
    transcript: string;
    summary: string;
    call_outcome: string;
    user_sentiment: string;
  } | null>(null);

  const [validationIssues, setValidationIssues] = useState<string[]>([]);
  const instructionsPreview = form.getFieldValue('behavior_instructions') || '';

  const [templates, setTemplates] = useState<AgentTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const { editAgent } = useAgentDetails({
    drawerOpen,
    token,
    agentId: agentData?.id ?? null,
  });

  useEffect(() => {
    if (editAgent && onAgentTypeLoaded) {
      onAgentTypeLoaded(editAgent.agent_type ?? null);
    }
  }, [editAgent, onAgentTypeLoaded]);

  useEffect(() => {
    onTestLoadingChange?.(testLoading);
  }, [testLoading, onTestLoadingChange]);

  const direction: Direction = useMemo(() => {
    if (isEditing) {
      const existingType =
        agentData?.agent_type || editAgent?.agent_type || null;

      if (existingType === 'Inbound' || existingType === 'Outbound') {
        return existingType;
      }
    }

    return initialChannelType === 'inbound' ? 'Inbound' : 'Outbound';
  }, [
    isEditing,
    agentData?.agent_type,
    editAgent?.agent_type,
    initialChannelType,
  ]);

  const lastGeneratedRef = useRef<{
    behavior_instructions: string;
    collect: (string | number)[];
  } | null>(null);

  const lastOptimizedResolutionRef = useRef<string | null>(null);

  useEffect(() => {
    if (!drawerOpen) return;
    if (!token) return;

    setTemplatesLoading(true);

    GetAgentTemplates(token)
      .then((res) => {
        const list = (res?.data?.data?.data ||
          res?.data?.data ||
          []) as AgentTemplate[];
        setTemplates(list.filter((t) => t.direction === direction));
      })
      .catch(() => message.error('Failed to load templates'))
      .finally(() => setTemplatesLoading(false));
  }, [drawerOpen, token, direction]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const collectIds = (form.getFieldValue('collect') || []) as (
    string | number
  )[];

  const collectedInfo = useMemo(() => {
    const ids = collectIds.map(String);
    if (!ids.length) return [];

    return ids
      .map((id) => {
        const fromRaw = rawCollectData?.find(
          (c) => String(c.id) === id
        )?.display_name;
        const fromOptions = collectOptions?.find(
          (o) => String(o.value) === id
        )?.label;
        return fromRaw || fromOptions || id;
      })
      .filter((x): x is string => !!x);
  }, [collectIds, rawCollectData, collectOptions]);

  const isInbound = useMemo(() => direction === 'Inbound', [direction]);

  const introPreview = useMemo(() => {
    return buildIntroPreview({
      agentName: watchedName,
      agentRole: watchedRole,
      companyName: aiCompanyName || companyName,
      selfIdentify,
      isInbound,
    });
  }, [
    watchedName,
    watchedRole,
    aiCompanyName,
    companyName,
    selfIdentify,
    isInbound,
  ]);

  const emailSignaturePreview = useMemo(() => {
    return buildEmailSignaturePreview({
      agentName: watchedName,
      agentRole: watchedRole,
      companyName: aiCompanyName || companyName,
      selfIdentify,
    });
  }, [watchedName, watchedRole, aiCompanyName, companyName, selfIdentify]);

  const { playingVoice, handleVoiceToggle } = useVoicePreview();
  const [presetVoice, setPresetVoice] = useState<VoiceOption | null>(null);

  const toPresetVoice = (
    v: ApiVoice | null | undefined
  ): VoiceOption | null => {
    if (!v?.id) return null;
    return {
      value: String(v.id),
      label: `${v.accent}, ${v.age}`,
      gender: v.gender,
      preview_location: v.preview_location,
      order: v?.order ?? null,
    } as VoiceOption;
  };

  const [dynOpen, setDynOpen] = useState(false);
  const [dynTargetField, setDynTargetField] = useState<
    'behavior_instructions' | 'success_custom_text' | null
  >(null);

  const usedVarIdsRef = useRef<Set<string>>(new Set());

  const openDynFor = (
    field: 'behavior_instructions' | 'success_custom_text'
  ) => {
    setDynTargetField(field);
    setDynOpen(true);
  };

  const insertVariableToken = (tokenStr: string, variableId: string) => {
    if (!dynTargetField) return;
    const current = (form.getFieldValue(dynTargetField) as string) || '';
    const next = current ? `${current}\n${tokenStr}` : tokenStr;
    form.setFieldsValue({ [dynTargetField]: next });
    if (variableId) usedVarIdsRef.current.add(variableId);
    setDynOpen(false);
  };

  const handleToggleCall = useCallback(
    (v: boolean) => {
      setCallEnabled(v);
      setCallExpanded(v);
      form.validateFields(['channel_guard']).catch(() => {});
    },
    [form]
  );

  const handleToggleEmail = useCallback(
    (v: boolean) => {
      setEmailEnabled(v);
      setEmailExpanded(v);
      form.validateFields(['channel_guard']).catch(() => {});
    },
    [form]
  );

  const handleGenerateInstructions = useCallback(
    async (
      reqId: number,
      input: { behavior_prompt: string; collect_ids: (string | number)[] }
    ) => {
      if (!token) {
        message.error('Not authenticated');
        throw new Error('Not authenticated');
      }

      const behaviorPrompt = String(input.behavior_prompt || '');
      const collectVariableIds = (input.collect_ids || []) as (
        string | number
      )[];

      if (!behaviorPrompt.trim()) {
        message.error('Please enter agent instructions first');
        throw new Error('Missing behavior prompt');
      }

      const collectVariableNames = collectVariableIds
        .map((id) => {
          const idStr = String(id);
          const fromRaw = rawCollectData?.find(
            (c) => String(c.id) === idStr
          )?.display_name;
          const fromOptions = collectOptions?.find(
            (o) => String(o.value) === idStr
          )?.label;
          return fromRaw || fromOptions;
        })
        .filter((name): name is string => !!name);

      const lastGenerated = lastGeneratedRef.current;

      const promptEdited = lastGenerated
        ? lastGenerated.behavior_instructions !== behaviorPrompt
        : false;

      const variablesEdited = lastGenerated
        ? JSON.stringify([...lastGenerated.collect].sort()) !==
          JSON.stringify([...collectVariableIds].sort())
        : false;

      const res = await ImproveAgentPrompt(token, {
        behavior_prompt: behaviorPrompt,
        collect_variables: collectVariableNames,
        ...(lastGenerated && { prompt_edited: promptEdited }),
        ...(lastGenerated && { variables_edited: variablesEdited }),
        is_inbound: direction === 'Inbound',
        introduction: introPreview,
      });

      if (reqId !== genReqIdRef.current) return;

      const data = res?.data?.data;

      if (data?.is_success) {
        const responseData = data?.data as GenerateResp;

        if (responseData?.generated_email) {
          setGeneratedEmailSubject(responseData.generated_email.subject || '');
          setGeneratedEmailBody(responseData.generated_email.email_body || '');
        }

        if (responseData?.call_transcript) {
          setGeneratedCallTranscript(
            responseData.call_transcript.transcript || ''
          );
        }

        lastGeneratedRef.current = {
          behavior_instructions: responseData.improved_prompt || behaviorPrompt,
          collect: collectVariableIds,
        };

        if (reqId === genReqIdRef.current) {
          message.success('Instructions generated successfully');
        }

        return responseData;
      }

      message.error(data?.message || 'Failed to generate instructions');
      throw new Error(data?.message || 'Failed to generate instructions');
    },
    [token, rawCollectData, collectOptions, direction, introPreview]
  );
  const [isOptimizingResolution, setIsOptimizingResolution] = useState(false);

  const handleImproveResolutionCriteria = useCallback(async () => {
    if (!token) {
      message.error('Not authenticated');
      throw new Error('Not authenticated');
    }

    const behaviorPrompt = form.getFieldValue('behavior_instructions');
    const customText = String(form.getFieldValue('success_custom_text') || '');

    if (!behaviorPrompt?.trim()) {
      message.error('Please enter agent instructions first');
      throw new Error('Missing behavior prompt');
    }

    if (!customText.trim()) {
      message.error('Please enter custom success criteria first');
      throw new Error('Missing resolution text');
    }

    setIsOptimizingResolution(true);
    try {
      const res = await ImproveResolutionPrompt(token, {
        resolution_text: customText,
        behavior_prompt: behaviorPrompt,
      });

      const data = res?.data?.data;
      if (data?.is_success) {
        const responseData = data?.data as {
          improved_text?: string;
          reason?: string;
        };

        if (responseData?.improved_text) {
          form.setFieldsValue({
            success_custom_text: responseData.improved_text,
          });
          lastOptimizedResolutionRef.current = responseData.improved_text;
          message.success('Success criteria improved');
        }
      } else {
        message.error(data?.message || 'Failed to improve success criteria');
        throw new Error(data?.message || 'Failed to improve success criteria');
      }
    } finally {
      setIsOptimizingResolution(false);
    }
  }, [token, form]);

  useEffect(() => {
    if (!drawerOpen) return;
    if (!callEnabled) setCallExpanded(false);
    if (!emailEnabled) setEmailExpanded(false);
  }, [drawerOpen, callEnabled, emailEnabled]);

  useEffect(() => {
    if (generatedEmailSubject || generatedEmailBody) {
      setTestResults(null);
      setTestResultsPolling(false);
    }
  }, [generatedEmailSubject, generatedEmailBody]);

  useEffect(() => {
    if (!drawerOpen) return;

    if (isEditing) {
      const first = Array.isArray(agentData?.channel)
        ? agentData.channel[0]
        : null;
      const current: ChannelType = first?.type === 'email' ? 'email' : 'phone';
      lockedChannelRef.current = current;
    } else {
      lockedChannelRef.current = createDefaultType;
    }
  }, [drawerOpen, isEditing, agentData, createDefaultType]);

  useImperativeHandle(ref, () => ({
    validate: async () => {
      const values = await form.validateFields();
      return values as AgentDrawerFormValues;
    },
    getCreatePayload: async () => {
      const values = (await form.validateFields()) as AgentDrawerFormValues;
      const payload = buildCreatePayload(values, {
        agentId: agentData?.id ?? null,
        channelType,
        selfIdentify,
        companyName: aiCompanyName || companyName || '',
        isInbound,
        collectShipmentId,
        customVarIds,
        pendingCustomVars,
        dynamicVariables: editAgent
          ? (editAgent.dynamic_variables ?? {})
          : undefined,
      });
      return {
        ...payload,
        channel_type: channelType,
        email_subject: generatedEmailSubject || undefined,
        email_body: generatedEmailBody || undefined,
        call_transcript: generatedCallTranscript || undefined,
      };
    },
    hasUnsavedChanges: () => {
      if (savedRef.current) return false;
      return dirtyRef.current;
    },
    triggerTest: async () => {
      const result = await handleTest();
      if (result !== false) setTestOpen(true);
    },
  }));

  useEffect(() => {
    if (!drawerOpen) return;
    savedRef.current = false;
    hydratedRef.current = false;
    dirtyRef.current = false;
    lastGeneratedRef.current = null;
    lastOptimizedResolutionRef.current = null;
    setGeneratedEmailSubject('');
    setGeneratedEmailBody('');
    setGeneratedCallTranscript('');
    setTestResults(null);
    setTestResultsPolling(false);

    // Fetch user name for email greeting
    if (token) {
      GetSettingsProfile(token)
        .then((res) => {
          const data = res?.data?.data?.data;
          setUserName(data?.name || '');
        })
        .catch(() => {
          setUserName('');
        });

      GetCompnayInfo(token)
        .then((res) => {
          const data = res?.data?.data?.data;
          setAiCompanyName(data?.ai_company_name || '');
        })
        .catch(() => {
          setAiCompanyName('');
        });
    }
  }, [drawerOpen, token]);

  useEffect(() => {
    if (!drawerOpen || !token) return;
    GetCompanyPhoneNumbers(
      token,
      direction === 'Inbound' ? 'Inbound' : undefined,
      isEditing ? agentData?.id : undefined
    )
      .then((res) => {
        const options: CompanyPhoneNumberOption[] = res?.data?.data?.data ?? [];
        setPhoneNumberOptions(options);
        if (!isEditing) {
          const defaultOption = options.find((n) => n.is_primary) ?? options[0];
          if (defaultOption)
            form.setFieldsValue({ phone_number: defaultOption.phone_number });
        }
      })
      .catch(() => setPhoneNumberOptions([]));
  }, [drawerOpen, token, direction, isEditing, agentData?.id, form]);

  const handlePurposeChange = () => {
    userChangedPurposeRef.current = true;
  };

  useEffect(() => {
    if (!drawerOpen) return;
    if (!dropdownsReady) return;

    if (isEditing) {
      if (!editAgent) return;

      const mapped = mapEditAgentToFormValues(editAgent);

      const channelsArr = Array.isArray(editAgent?.channel)
        ? editAgent.channel
        : [];

      const phoneFromChannel =
        channelsArr.find((c) => c?.type === 'phone')?.value || '';
      const emailFromChannel =
        channelsArr.find((c) => c?.type === 'email')?.value || '';

      const phoneValue = String(
        editAgent?.phone_number || phoneFromChannel || ''
      );
      const emailValue = String(editAgent?.email || emailFromChannel || '');

      const hasPhone = !!phoneValue.trim();
      const hasEmail = !!emailValue.trim();

      const nextType: ChannelType = hasEmail && !hasPhone ? 'email' : 'phone';

      setPresetVoice(toPresetVoice(editAgent?.voice));
      setChannelType(nextType);

      setCallEnabled(hasPhone);
      setEmailEnabled(hasEmail);
      setCollectShipmentId(editAgent?.collect_shipment_id ?? false);

      setGeneratedEmailSubject(editAgent?.email_subject || '');
      setGeneratedEmailBody(editAgent?.email_body || '');
      setGeneratedCallTranscript(editAgent?.call_transcript || '');

      form.setFieldsValue({
        ...(mapped as AgentDrawerFormValues),
        channel_type: nextType,
        phone_number: phoneValue,
        email: 'customer@zoomlogi.com',
        escalation_phone_number: editAgent?.escalation_phone_number || '',
        cc_email: editAgent?.cc_email || '',
      } as AgentDrawerFormValues);

      setCallExpanded(hasPhone);
      setEmailExpanded(hasEmail);

      lastGeneratedRef.current = {
        behavior_instructions: mapped.behavior_instructions || '',
        collect: mapped.collect || [],
      };

      hydratedRef.current = true;
      dirtyRef.current = false;
      return;
    }

    const defaults = getDefaultFormValues();
    const nextType: ChannelType = createDefaultType;

    setChannelType(nextType);
    setCallEnabled(false);
    setEmailEnabled(false);
    setCollectShipmentId(false);
    setCallExpanded(false);
    setEmailExpanded(false);

    setGeneratedEmailSubject('');
    setGeneratedEmailBody('');
    setGeneratedCallTranscript('');

    form.resetFields();
    form.setFieldsValue({
      ...(defaults as AgentDrawerFormValues),
      purpose: '',
      channel_type: nextType,
      email: 'customer@zoomlogi.com',
    } as AgentDrawerFormValues);

    setPresetVoice(null);
    if (token)
      GetAiAgentVoicesWithSearchAndPagination(token, '', 1)
        .then((res) => {
          const voices: ApiVoice[] = res?.data?.data?.data ?? [];
          const defaultVoice = voices.find((v) => v.order === 19);
          if (defaultVoice) {
            setPresetVoice(toPresetVoice(defaultVoice));
            form.setFieldsValue({ voice: String(defaultVoice.id) });
          }
        })
        .catch(() => setPresetVoice(null));

    hydratedRef.current = true;
    dirtyRef.current = false;
    usedVarIdsRef.current.clear();
  }, [
    drawerOpen,
    dropdownsReady,
    isEditing,
    editAgent,
    form,
    purposeOptions,
    createDefaultType,
    token,
  ]);
  useEffect(() => {
    if (!drawerOpen) return;

    if (isEditing) {
      setSuccessCollapsed(false);
    } else {
      setSuccessCollapsed(true);
    }
  }, [drawerOpen, isEditing]);

  const setSelfIdentify = (next: boolean) => {
    form.setFieldsValue({ self_identify: next });
  };

  const postUsedVars = async () => {
    if (!token) return;
    const ids = Array.from(usedVarIdsRef.current);
    if (!ids.length) return;

    try {
      await PostAiAgentDynamicVariablesUsed(token, ids);
    } catch {
      message.error('Failed to save used dynamic variables');
    }
  };

  const onFinish = async (values: AgentDrawerFormValues) => {
    if (!token) {
      onClose();
      return;
    }

    if (saving) return;

    if (shouldPromptForInstructionsBeforeSave(isDefaultAgent, values.collect)) {
      setInstrOpen(true);
      return;
    }

    setSaving(true);

    const payload = buildCreatePayload(values, {
      agentId: agentData?.id ?? null,
      channelType,
      selfIdentify,
      companyName: aiCompanyName || companyName || '',
      isInbound,
      collectShipmentId,
      customVarIds,
      pendingCustomVars,
      dynamicVariables: editAgent
        ? (editAgent.dynamic_variables ?? {})
        : undefined,
    });

    const payloadWithType = {
      ...payload,
      agent_type: direction,
      channel_type: channelType,
      email_subject: generatedEmailSubject || undefined,
      email_body: generatedEmailBody || undefined,
      call_transcript: generatedCallTranscript || undefined,
    };
    const { id, ...rest } = payloadWithType;

    if (id) {
      UpdateAgent(token, String(id), rest)
        .then(async (res) => {
          const data = res?.data?.data;
          if (data?.is_success) {
            await postUsedVars();
            message.success(data?.message || 'Saved successfully');
            onSaved?.();
            savedRef.current = true;
            onClose();
          } else {
            message.error(data?.message || 'Error saving agent');
          }
        })
        .catch(() => message.error('Error saving agent'))
        .finally(() => {
          setSaving(false);
        });
    } else {
      const { id: _id, ...createPayload } = payloadWithType;

      CreateAgent(token, {
        ...createPayload,
        ...(templateIdRef.current ? { template: templateIdRef.current } : {}),
      })
        .then(async (res) => {
          const data = res?.data?.data;
          if (data?.is_success) {
            await postUsedVars();
            message.success(data?.message || 'Saved successfully');
            onSaved?.();
            savedRef.current = true;
            onClose();
          } else {
            message.error(data?.message || 'Error saving agent');
          }
        })
        .catch(() => message.error('Error saving agent'))
        .finally(() => {
          setSaving(false);
        });
    }
  };
  const handleTest = async () => {
    if (!token) {
      message.error('Not authenticated');
      return false;
    }

    try {
      await form.validateFields();
      return true;
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        message.error('Please fill in all required fields');
      }
      return false;
    }
  };

  const pollForTestResults = useCallback(
    (workflowEventUuid: string) => {
      if (!token) return;

      setTestLoading(false);
      setTestResultsPolling(true);
      setTestResults(null);

      const maxAttempts = 60;
      const pollInterval = 10000;
      let attempts = 0;

      const poll = async () => {
        try {
          const res = await GetTestAgentResult(token, workflowEventUuid);
          const data = res?.data?.data?.data as
            | {
                status?: string;
                issue_resolved?: boolean;
                pca_results?: Record<string, unknown> | null;
                transcript?: string;
                summary?: string;
                call_outcome?: string;
                user_sentiment?: string;
              }
            | undefined;

          if (data && data.status === 'COMPLETED') {
            setTestResults({
              status: data.status || '',
              issue_resolved: data.issue_resolved || false,
              pca_results: data.pca_results || null,
              transcript: data.transcript || '',
              summary: data.summary || '',
              call_outcome: data.call_outcome || '',
              user_sentiment: data.user_sentiment || '',
            });
            setTestResultsPolling(false);
            setTestLoading(false);
            message.success('Test results received');
            return;
          }

          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, pollInterval);
          } else {
            setTestResultsPolling(false);
            setTestLoading(false);
            message.warning(
              'Test is taking longer than expected. Results may still be processing.'
            );
          }
        } catch {
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, pollInterval);
          } else {
            setTestResultsPolling(false);
            setTestLoading(false);
            message.error('Failed to retrieve test results');
          }
        }
      };

      poll().catch(() => {
        setTestResultsPolling(false);
        setTestLoading(false);
        message.error('Failed to retrieve test results');
      });
    },
    [token]
  );

  const handleClearTestResults = useCallback(() => {
    setTestResults(null);
    setTestResultsPolling(false);
  }, []);

  const handleRunTest = async (payload: {
    channel: 'call' | 'email';
    phone?: string;
    email?: string;
  }) => {
    if (!token) return;

    setTestResults(null);
    setTestResultsPolling(false);

    if (payload.channel === 'call' && payload.phone) {
      try {
        const values = await form.validateFields();

        const testPayload = buildTestPayload(values, {
          channelType,
          selfIdentify,
          companyName: aiCompanyName || companyName || '',
          isInbound,
          rawEscalationData,
          rawCollectData,
          toNumber: payload.phone,
        });

        setTestLoading(true);
        const res = await TestAgent(token, testPayload);
        const data = res?.data?.data as
          | {
              is_success?: boolean;
              message?: string;
              data?: { workflow_event_uuid?: string };
            }
          | undefined;

        if (data?.is_success) {
          message.success(data?.message || 'Test call initiated successfully');
          const workflowEventUuid = data?.data?.workflow_event_uuid;
          if (workflowEventUuid) {
            pollForTestResults(workflowEventUuid);
          } else {
            setTestLoading(false);
          }
        } else {
          message.error(data?.message || 'Failed to initiate test call');
          setTestLoading(false);
        }
      } catch {
        message.error('Failed to initiate test call');
        setTestLoading(false);
      }
    } else if (payload.channel === 'email' && payload.email) {
      try {
        const values = await form.validateFields();

        if (!generatedEmailSubject || !generatedEmailBody) {
          message.error(
            'Please generate instructions first to create email content. Click the "Generate instructions" button in the Agent Behavior section.'
          );
          return;
        }

        const agentName = values.agent_name || 'Agent';
        const agentRole = values.ai_agent_role || 'Assistant';

        const greeting = userName ? `Hi ${userName},<br><br>` : '';
        const signature = `<br><br>Thanks,<br>${agentName}, ${agentRole}<br>${
          companyName || 'Company'
        }`;

        const emailBodyWithGreetingAndSignature =
          greeting + generatedEmailBody + signature;

        const emailPayload = buildTestEmailPayload(values, {
          channelType,
          selfIdentify,
          companyName: aiCompanyName || companyName || '',
          isInbound,
          rawEscalationData,
          rawCollectData,
          toEmail: payload.email,
          emailSubject: generatedEmailSubject,
          emailBodyHtml: emailBodyWithGreetingAndSignature,
          ccEmail: values.cc_email,
        });

        setTestLoading(true);
        const res = await TestEmailAgent(token, emailPayload);
        const data = res?.data?.data as
          | {
              is_success?: boolean;
              message?: string;
              data?: { workflow_event_uuid?: string };
            }
          | undefined;

        if (data?.is_success) {
          message.success(data?.message || 'Test email sent successfully');
          const workflowEventUuid = data?.data?.workflow_event_uuid;
          if (workflowEventUuid) {
            pollForTestResults(workflowEventUuid);
          } else {
            setTestLoading(false);
          }
        } else {
          message.error(data?.message || 'Failed to send test email');
          setTestLoading(false);
        }
      } catch {
        message.error('Failed to send test email');
        setTestLoading(false);
      }
    }
  };

  const watchedCollect = Form.useWatch('collect', form) as
    (string | number)[] | undefined;

  const allowedCollectSet = useMemo(() => {
    return new Set((watchedCollect || []).map((x) => String(x)));
  }, [watchedCollect]);

  const customVarIds = useMemo(() => {
    return new Set(
      (collectOptions || [])
        .filter((o) => o.isCustomVar)
        .map((o) => String(o.value))
    );
  }, [collectOptions]);

  // Custom variables added this session but not yet saved as AIPCACustomVar
  // rows - created (and linked to the agent) only when the agent is saved.
  const pendingCustomVars = useMemo(() => {
    const map: Record<string, NewCustomVarInput> = {};
    (collectOptions || [])
      .filter((o) => o.isPending)
      .forEach((o) => {
        map[String(o.value)] = {
          display_name: o.label,
          type: o.type || 'TEXT',
          description: o.description || '',
          sample_value: o.sample_value || '',
        };
      });
    return map;
  }, [collectOptions]);

  const filteredCollectOptions = useMemo(() => {
    return (collectOptions || []).filter((o) =>
      allowedCollectSet.has(String(o.value))
    );
  }, [collectOptions, allowedCollectSet]);

  useEffect(() => {
    const selected =
      (form.getFieldValue('success_collect_selected') as (string | number)[]) ||
      [];

    const next = selected
      .map((x) => String(x))
      .filter((id) => allowedCollectSet.has(id));
    const prevNorm = selected.map(String).sort().join('|');
    const nextNorm = [...next].sort().join('|');

    if (prevNorm !== nextNorm) {
      form.setFieldsValue({ success_collect_selected: next });
    }
  }, [allowedCollectSet, form]);
  return (
    <Form
      form={form}
      layout='vertical'
      disabled={readOnly}
      initialValues={
        agentData?.id
          ? { ...getDefaultFormValues(), agent_name: '' }
          : getDefaultFormValues()
      }
      onFinish={onFinish}
      onFinishFailed={({ errorFields }) => {
        const fields = errorFields || [];

        const isRelevant = (namePath: (string | number)[]) => {
          const key = String(namePath?.[0] ?? '');
          return (
            key === 'channel_guard' ||
            key === 'behavior_instructions' ||
            key === 'success_guard'
          );
        };

        const relevantMsgs = fields
          .filter((f) => isRelevant(f.name as (string | number)[]))
          .flatMap((f) => (Array.isArray(f.errors) ? f.errors : []));
        setValidationIssues(relevantMsgs);
        const hasChannelError = fields.some((f) =>
          String(f.name?.[0]).includes('channel_guard')
        );
        if (hasChannelError) {
          setCallExpanded(true);
          setEmailExpanded(true);
        }

        const hasSuccessError = fields.some((f) =>
          String(f.name?.[0]).includes('success_guard')
        );
        if (hasSuccessError) setSuccessCollapsed(false);
        if (relevantMsgs.length) {
          message.error('Please fix the required sections and try again.');
        }
      }}
      className='agentForm'
      onValuesChange={() => {
        if (!hydratedRef.current) return;
        dirtyRef.current = true;
        setValidationIssues([]);
      }}
    >
      <Form.Item
        name='behavior_instructions'
        hidden
        rules={[
          {
            validator: async (_, value) =>
              validateBehaviorInstructions(
                isDefaultAgent,
                Boolean(form.getFieldValue('advanced_instruction_mode')),
                value
              ),
          },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item name='advanced_instruction_mode' hidden>
        <Input type='hidden' />
      </Form.Item>

      <Form.Item name='collect' hidden>
        <Input />
      </Form.Item>

      <Form.Item name='channel_type' hidden>
        <Input type='hidden' />
      </Form.Item>
      <Form.Item
        name='success_guard'
        hidden
        dependencies={[
          'success_mode',
          'success_info_collected',
          'success_no_human',
          'success_positive',
          'success_collect_selected',
          'success_custom_text',
        ]}
        rules={[
          {
            validator: async () =>
              validateSuccessCriteria(isDefaultAgent, {
                mode: (form.getFieldValue('success_mode') || 'specific') as
                  'specific' | 'custom',
                infoCollected: !!form.getFieldValue('success_info_collected'),
                noHuman: !!form.getFieldValue('success_no_human'),
                positive: !!form.getFieldValue('success_positive'),
                selected:
                  (form.getFieldValue(
                    'success_collect_selected'
                  ) as string[]) || [],
                customText: String(
                  form.getFieldValue('success_custom_text') || ''
                ),
              }),
          },
        ]}
      >
        <Input type='hidden' />
      </Form.Item>
      <Form.Item
        name='channel_guard'
        hidden
        dependencies={['phone_number', 'email']}
        rules={[
          {
            validator: async () => {
              if (!callEnabled && !emailEnabled) {
                return Promise.reject(
                  new Error('Enable at least one channel: Call or Email.')
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input type='hidden' />
      </Form.Item>
      <Form.Item name='self_identify' hidden>
        <Input type='hidden' />
      </Form.Item>

      <Form.Item name='success_mode' hidden>
        <Input />
      </Form.Item>
      <Form.Item name='success_info_collected' hidden>
        <Input />
      </Form.Item>
      <Form.Item name='success_no_human' hidden>
        <Input />
      </Form.Item>
      <Form.Item name='success_positive' hidden>
        <Input />
      </Form.Item>
      <Form.Item name='success_collect_selected' hidden>
        <Input />
      </Form.Item>
      <Form.Item name='success_custom_text' hidden>
        <Input />
      </Form.Item>

      {!hideAgentHeader && (
        <AgentHeader
          agentType={agentData?.agent_type ?? editAgent?.agent_type ?? null}
          isEditing={isEditing}
          readOnly={readOnly}
          onTest={handleTest}
          onRunTest={handleRunTest}
          onClearTestResults={handleClearTestResults}
          testLoading={testLoading}
          createType={selectedCreateType}
          callEnabled={callEnabled}
          emailEnabled={emailEnabled}
          testResultsPolling={testResultsPolling}
          testResults={testResults}
        />
      )}

      {hideAgentHeader && (
        <SetupAgentTestModal
          open={testOpen}
          setOpen={setTestOpen}
          loading={!!testLoading}
          defaultChannel={callEnabled ? 'call' : 'email'}
          callEnabled={callEnabled}
          emailEnabled={emailEnabled}
          testResultsPolling={testResultsPolling}
          testResults={testResults}
          onSubmit={handleRunTest}
          onClose={handleClearTestResults}
        />
      )}

      <div
        className={[
          'agentFormBody custom-scrollbar',
          hideAgentHeader && layout.noTopPadding,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <CustomTitle
              text={'General settings'}
              color='var(--text-primary)'
              fontSize='16px'
              lineHeight='28px'
              fontWeight='600'
              marginBottom={16}
              marginTop={12}
            />
            <Form.Item
              label='AI agent name'
              className='custom-input form-input-style'
              name='agent_name'
              rules={[{ required: true, message: 'Please enter agent name' }]}
            >
              <CustomInput
                bgColor='var(--light-gray)'
                height='36px'
                textColor='var(--text-primary)'
                borderRadius='8px'
                border='none'
                placeholderColor='var(--text-secondary)'
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label='Purpose'
              className='custom-input form-input-style'
              name='purpose'
              rules={[{ required: true, message: 'Please enter a purpose' }]}
            >
              <CustomInput
                bgColor='var(--light-gray)'
                height='36px'
                textColor='var(--text-primary)'
                borderRadius='8px'
                border='none'
                placeholderColor='var(--text-secondary)'
                disabled={readOnly || isDefaultAgent}
                onChange={handlePurposeChange}
              />
            </Form.Item>
            <Paragraph className='agentFormHelpText'>
              What is this agent trying to accomplish? (example: Pickup check)
            </Paragraph>
          </Col>

          <IdentitySection
            selfIdentify={selfIdentify}
            onToggleSelfIdentify={() => setSelfIdentify(!selfIdentify)}
            readOnly={readOnly}
          />

          {!isDefaultAgent && (
            <>
              <AgentBehaviorSection
                defaultCollapsed={false}
                onAddInstructions={() => setInstrOpen(true)}
                instructionsPreview={instructionsPreview}
                collectedInfo={collectedInfo}
                isEditing={isEditing}
                readOnly={readOnly}
                dynVarOptions={dynVarOptions}
              />

              <SuccessSection
                key={successKey}
                form={form}
                defaultCollapsed={successCollapsed}
                loadingDropdowns={loadingDropdowns}
                collectOptions={filteredCollectOptions.map((x) => ({
                  value: x.value,
                  label: x.label,
                }))}
                onOptimize={handleImproveResolutionCriteria}
                behaviorInstructions={watchedBehaviorInstructions}
                isOptimizing={isOptimizingResolution}
                lastOptimizedResolution={lastOptimizedResolutionRef.current}
                readOnly={readOnly}
              />

              <AgentInstructionsModal
                open={instrOpen}
                onClose={() => {
                  setInstrOpen(false);
                  cancelAllPendingUsageConfirms();
                }}
                onSave={() => {
                  setInstrOpen(false);
                  cancelAllPendingUsageConfirms();
                  setSuccessCollapsed(false);
                  setSuccessKey((k) => k + 1);
                }}
                form={form}
                dynVarOptions={dynVarOptions}
                onOpenDyn={() => openDynFor('behavior_instructions')}
                loadingDropdowns={loadingDropdowns}
                collectOptions={collectOptions.map((x) => ({
                  value: x.value,
                  label: x.label,
                  type: x.type,
                  isCustomVar: x.isCustomVar,
                  description: x.description,
                  sample_value: x.sample_value,
                  is_in_use: x.is_in_use,
                  isPending: x.isPending,
                }))}
                direction={direction}
                templates={templates}
                templatesLoading={templatesLoading}
                onSelectTemplate={(tpl) => {
                  templateIdRef.current = tpl.id;
                  lastGeneratedRef.current = {
                    behavior_instructions:
                      form.getFieldValue('behavior_instructions') || '',
                    collect: form.getFieldValue('collect') || [],
                  };
                }}
                genReqIdRef={genReqIdRef}
                onGenerateInstructions={handleGenerateInstructions}
                isEditing={isEditing}
                emailSignature={emailSignaturePreview}
                companyName={aiCompanyName || companyName}
                onDeleteCustomVar={async (id) => {
                  if (!token) return;
                  try {
                    await DeleteCustomCollectVar(id, token);
                    setCollectOptions((prev) =>
                      prev.filter((o) => String(o.value) !== id)
                    );
                  } catch (err) {
                    const reason = (
                      err as {
                        data?: { data?: { data?: { reason?: string } } };
                      }
                    )?.data?.data?.data?.reason;
                    if (reason === 'in_use') {
                      throw new Error(CUSTOM_VAR_IN_USE_ERROR);
                    }
                    message.error('Failed to delete custom variable');
                    throw err;
                  }
                }}
                onCreateCustomVar={async (input) => {
                  // Not saved to the DB yet - this only stages the variable
                  // locally. It's created (and linked to this agent) when the
                  // agent instructions are actually saved; see
                  // pendingCustomVars/buildCreatePayload above. This also
                  // means removing it before save is a pure local operation
                  // (see CheckboxMultiSelect's isPending check on delete).
                  const name = input.name.trim();
                  const nameTaken = (collectOptions || []).some(
                    (o) => o.label.trim().toLowerCase() === name.toLowerCase()
                  );
                  if (nameTaken) {
                    message.error('A variable with this name already exists');
                    throw new Error('Duplicate variable name');
                  }
                  const tempId = `pending-${crypto.randomUUID()}`;
                  setCollectOptions((prev) => [
                    ...prev,
                    {
                      value: tempId,
                      label: name,
                      type: input.type,
                      isCustomVar: true,
                      isPending: true,
                      description: input.description,
                      sample_value: input.sample_value,
                    } as (typeof prev)[number],
                  ]);
                  return tempId;
                }}
                onUpdateCustomVar={async (id, input) => {
                  const idStr = String(id);
                  const applyLocally = () =>
                    setCollectOptions(
                      (prev) =>
                        prev.map((o) =>
                          String(o.value) === idStr
                            ? ({
                                ...o,
                                label: input.name,
                                type: input.type,
                                description: input.description,
                                sample_value: input.sample_value,
                              } as (typeof prev)[number])
                            : o
                        ) as typeof prev
                    );

                  // Not yet created server-side - stays a pure local update,
                  // same as onCreateCustomVar; it's persisted (with these
                  // values) only when the agent itself is saved.
                  const option = (collectOptions || []).find(
                    (o) => String(o.value) === idStr
                  );
                  if (option?.isPending) {
                    applyLocally();
                    return;
                  }

                  if (!token) {
                    message.error('Failed to update custom variable');
                    throw new Error('Missing auth token');
                  }
                  try {
                    // Only a type change can affect workflows that use the
                    // variable: it can make a filter's operators invalid.
                    // A rename keeps the reference name workflows store.
                    const typeChanged = input.type !== option?.type;
                    if (typeChanged) {
                      const usage = await GetCustomCollectVarUsage(
                        idStr,
                        token
                      );
                      const workflows =
                        usage?.data?.data?.data?.workflows ?? [];
                      if (workflows.length > 0) {
                        const proceed = await confirmDespiteUsage(
                          option?.label ?? input.name,
                          workflows
                        );
                        if (!proceed) {
                          throw new Error(CUSTOM_VAR_EDIT_CANCELLED);
                        }
                      }
                    }

                    await UpdateCustomCollectVar(
                      idStr,
                      {
                        display_name: input.name,
                        type: input.type,
                        description: input.description,
                        sample_value: input.sample_value,
                      },
                      token
                    );
                    applyLocally();
                  } catch (err) {
                    if (!(
                      err instanceof Error &&
                      err.message === CUSTOM_VAR_EDIT_CANCELLED
                    )) {
                      message.error('Failed to update custom variable');
                    }
                    throw err;
                  }
                }}
              />
            </>
          )}

          <Divider className='agentFormDivider' />
          <div className='agentRetrieveShipmentRow'>
            <div className='agentRetrieveShipmentRow__left'>
              <svg
                width='14'
                height='11'
                viewBox='0 0 14 11'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className={layout.checkboxAlign}
              >
                <path
                  d='M1.33333 2V3.33333C1.33333 3.7 1.03333 4 0.666667 4H0V6.66667H0.666667C1.03333 6.66667 1.33333 6.96667 1.33333 7.33333V8.66667C1.33333 9.76667 2.23333 10.6667 3.33333 10.6667H5.33333V9.33333H3.33333C2.96667 9.33333 2.66667 9.03333 2.66667 8.66667V7.33333C2.66667 6.46667 2.10667 5.72 1.33333 5.44667V5.22C2.10667 4.94667 2.66667 4.2 2.66667 3.33333V2C2.66667 1.63333 2.96667 1.33333 3.33333 1.33333H5.33333V0H3.33333C2.23333 0 1.33333 0.9 1.33333 2Z'
                  fill='var(--text-primary)'
                />
                <path
                  d='M12.6667 4C12.3 4 12 3.7 12 3.33333V2C12 0.9 11.1 0 10 0H8V1.33333H10C10.3667 1.33333 10.6667 1.63333 10.6667 2V3.33333C10.6667 4.2 11.2267 4.94667 12 5.22V5.44667C11.2267 5.72 10.6667 6.46 10.6667 7.33333V8.66667C10.6667 9.03333 10.3667 9.33333 10 9.33333H8V10.6667H10C11.1 10.6667 12 9.76667 12 8.66667V7.33333C12 6.96667 12.3 6.66667 12.6667 6.66667H13.3333V4H12.6667Z'
                  fill='var(--text-primary)'
                />
              </svg>
              <div>
                <div className='agentFormToggleLabel'>
                  Retrieve Shipment Info via MCP
                </div>
                <p
                  className={['agentFormHelpText', layout.helpSpacing]
                    .filter(Boolean)
                    .join(' ')}
                >
                  Matches the conversation to a shipment for reference in later
                  steps
                </p>
              </div>
            </div>
            <ToggleSwitch
              label=''
              checked={collectShipmentId}
              onChange={(e) => setCollectShipmentId(e.target.checked)}
              disabled={readOnly || isDefaultAgent}
            />
          </div>

          <CallSection
            expanded={callExpanded}
            onToggleExpanded={() => setCallExpanded((p) => !p)}
            enabled={callEnabled}
            onToggleEnabled={handleToggleCall}
            isDefaultAgent={isDefaultAgent}
            form={form}
            introPreview={introPreview}
            loadingDropdowns={loadingDropdowns}
            presetVoice={presetVoice}
            playingVoice={playingVoice}
            onToggleVoice={handleVoiceToggle}
            escalateWhenOptions={escalateWhenOptions.map((x) => ({
              value: x.value,
              label: x.label,
            }))}
            phoneNumberOptions={phoneNumberOptions}
            showKeypadOption={!isInbound}
            readOnly={readOnly}
          />

          <EmailSection
            expanded={emailExpanded}
            onToggleExpanded={() => setEmailExpanded((p) => !p)}
            enabled={emailEnabled}
            onToggleEnabled={handleToggleEmail}
            isDefaultAgent={isDefaultAgent}
            emailSignaturePreview={emailSignaturePreview}
            readOnly={readOnly}
          />
        </Row>
      </div>

      <AgentFooter
        onClose={onClose}
        validationIssues={validationIssues}
        saving={saving}
        readOnly={readOnly}
      />

      <DynamicVariablesModal
        open={dynOpen}
        onClose={() => setDynOpen(false)}
        onInsert={insertVariableToken}
      />

      <CustomVariableUsageWarningModal
        visible={pendingUsageConfirm !== null}
        variableName={pendingUsageConfirm?.variableName ?? ''}
        workflows={pendingUsageConfirm?.workflows ?? []}
        onCancel={() => resolveFrontUsageConfirm(false)}
        onContinue={() => resolveFrontUsageConfirm(true)}
      />
    </Form>
  );
});

export default SettingsAgentDrawerForm;
